import { ApiError } from './errors';
import type {
  MoodleCourseSection,
  MoodleAttemptData,
  MoodleAttemptReview,
  MoodleFeedbackItem,
  LmsUser,
  MoodleCourse,
  MoodleQuizCompositeStatus,
  FeedbackAccessInfo,
} from '@/types/moodle.types';

let BASE =
  (process.env.NEXT_PUBLIC_COMMUNITY_MANAGER_URL || 'https://api.spreadd.io/community-manager') +
  '/community-manager/v1/course/lms';

export const setLmsClientBaseUrl = (url: string) => {
  if (url) {
    BASE = url + '/community-manager/v1/course/lms';
  }
};

// ── Request Deduplication ───────────────────────────────────────────────────
// Stores in-flight promises to prevent multiple identical requests from firing
// at the same time (e.g. during Strict Mode double-renders or simultaneous mounts).
const pendingRequests = new Map<string, Promise<any>>();

async function req<T>(path: string, options: RequestInit = {}): Promise<T> {
  const isGet = !options.method || options.method.toUpperCase() === 'GET';
  const cacheKey = `${isGet ? 'GET' : options.method}:${path}`;

  // Only deduplicate GET requests
  if (isGet && pendingRequests.has(cacheKey)) {
    return pendingRequests.get(cacheKey) as Promise<T>;
  }

  const requestPromise = (async () => {
    try {
      const token = typeof window !== 'undefined' ? localStorage.getItem('lms_token') : null;
      const res = await fetch(`${BASE}${path}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...options.headers,
        },
      });

      const json = await res.json();
      if (!res.ok) {
        // ── Global 401 Handler ──
        // If we get an unauthorized error, we should force a logout
        // We only redirect if we're not already on the login page to avoid loops
        if (
          res.status === 401 &&
          typeof window !== 'undefined' &&
          !window.location.pathname.includes('/auth/login')
        ) {
          console.warn('Unauthorized request detected. Redirecting to login...');
          // Clear local storage and force redirect
          localStorage.removeItem('lms_token');
          localStorage.removeItem('lms_user_info');
          document.cookie = 'lms_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
          window.location.href = '/auth/login';
        }

        throw new ApiError(
          json.error || `Request failed: ${res.status}`,
          res.status,
          json.code,
          res.status >= 500 // Retry on server errors
        );
      }
      return json.data as T;
    } finally {
      // Always clear the pending request when finished
      if (isGet) pendingRequests.delete(cacheKey);
    }
  })();

  if (isGet) {
    pendingRequests.set(cacheKey, requestPromise);
  }

  return requestPromise;
}

export const lmsClient = {
  // ── Auth ────────────────────────────────────────────────────────────────────
  auth: {
    login: async (username: string, password: string): Promise<LmsUser> => {
      const res = await fetch(`${BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Login failed');
      return json.data as LmsUser;
    },
    logout: async (): Promise<void> => {
      const token = typeof window !== 'undefined' ? localStorage.getItem('lms_token') : null;
      const res = await fetch(`${BASE}/auth/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!res.ok) throw new Error('Logout failed');
    },
  },

  // ── Courses ─────────────────────────────────────────────────────────────────
  courses: {
    getCourses: () => req<MoodleCourse[]>('/courses'),

    getCategories: () => req<any[]>('/categories'),

    getCourseContents: (courseId: number) => req<MoodleCourseSection[]>(`/${courseId}/contents`),

    viewCourse: (courseId: number) => req<unknown>(`/${courseId}/view`, { method: 'POST' }),

    getCoursePage: (pageId: number, courseId: number) =>
      req<unknown>(`/page/${pageId}?courseId=${courseId}`),

    getCourseProgress: (courseId: number) =>
      req<{ progress: number | null }>(`/${courseId}/progress`),
  },

  // ── Completion ──────────────────────────────────────────────────────────────
  completion: {
    getActivityStatus: (courseId: number) =>
      req<{ statuses: { cmid: number; state: number }[] }>(`/${courseId}/completion`),

    markActivityComplete: (courseId: number, cmId: number) =>
      req<unknown>(`/${courseId}/completion/${cmId}`, { method: 'POST' }),
  },

  // ── Grades ──────────────────────────────────────────────────────────────────
  grades: {
    getStudentGrades: (courseId: number) => req<unknown>(`/${courseId}/grades`),
  },

  // ── Quiz ────────────────────────────────────────────────────────────────────
  quiz: {
    getCompositeStatus: (courseId: number, quizId: number) =>
      req<MoodleQuizCompositeStatus>(`/quiz/${quizId}/status?courseId=${courseId}`),

    startAttempt: (
      quizId: number,
      courseId: number,
      preflightData: { name: string; value: string }[] = []
    ) =>
      req<MoodleAttemptData>(`/quiz/${quizId}/start?courseId=${courseId}`, {
        method: 'POST',
        body: JSON.stringify({ preflightData }),
      }),

    getAttemptData: (attemptId: number, page: number, courseId: number) =>
      req<MoodleAttemptData>(`/quiz/attempt/${attemptId}/page/${page}?courseId=${courseId}`),

    savePage: async (
      attemptId: number,
      nextPageOrAnswers: number | { name: string; value: string }[],
      answersOrCourseId: { name: string; value: string }[] | number,
      courseId?: number
    ): Promise<MoodleAttemptData | null> => {
      let finalNextPage: number | undefined;
      let finalAnswers: { name: string; value: string }[];
      let finalCourseId: number;

      if (Array.isArray(nextPageOrAnswers)) {
        // savePage(attemptId, answers, courseId)
        finalAnswers = nextPageOrAnswers;
        finalCourseId = answersOrCourseId as number;
      } else {
        // savePage(attemptId, nextPage, answers, courseId)
        finalNextPage = nextPageOrAnswers;
        finalAnswers = answersOrCourseId as { name: string; value: string }[];
        finalCourseId = courseId as number;
      }

      // 1. Save the answers for the current page
      let answersToSend = [...finalAnswers];
      if (finalNextPage !== undefined && finalNextPage >= 0) {
        // Filter out any existing nextpage parameter to prevent duplicates
        answersToSend = answersToSend.filter((a) => a.name !== 'nextpage');
        // Push the correct target page value
        answersToSend.push({ name: 'nextpage', value: String(finalNextPage) });
      }

      // 1. Save and navigate in a single request to prevent load balancing or replica DB lag issues
      const queryParams = [`courseId=${finalCourseId}`];
      if (finalNextPage !== undefined && finalNextPage >= 0) {
        queryParams.push(`nextPage=${finalNextPage}`);
      }

      const response = await req<unknown>(
        `/quiz/attempt/${attemptId}/save?${queryParams.join('&')}`,
        {
          method: 'POST',
          body: JSON.stringify({ answers: answersToSend }),
        }
      );

      // Return the nextPage MoodleAttemptData returned directly in the save response
      if (finalNextPage !== undefined && finalNextPage >= 0) {
        return response as MoodleAttemptData;
      }
      return null;
    },

    submit: (attemptId: number, answers: { name: string; value: string }[], courseId: number) =>
      req<unknown>(`/quiz/attempt/${attemptId}/submit?courseId=${courseId}`, {
        method: 'POST',
        body: JSON.stringify({ answers }),
      }),

    getAttemptReview: (attemptId: number, courseId: number, page: number = -1) =>
      req<MoodleAttemptReview>(
        `/quiz/attempt/${attemptId}/review?courseId=${courseId}&page=${page}`
      ),

    getAttemptAccessInformation: (quizId: number, courseId: number, attemptId: number) =>
      req<{ endtime?: number }>(
        `/quiz/${quizId}/access-info?courseId=${courseId}&attemptId=${attemptId}`
      ),
  },

  // ── Feedback ────────────────────────────────────────────────────────────────
  feedback: {
    getStatus: (feedbackId: number, courseId: number) =>
      req<{
        accessInfo: FeedbackAccessInfo;
        isAlreadySubmitted: boolean;
        isAnonymous: boolean;
        description: string;
      }>(`/feedback/${feedbackId}/status?courseId=${courseId}`),

    getAccessInfo: (feedbackId: number, courseId: number) =>
      req<any>(`/feedback/${feedbackId}/access-info?courseId=${courseId}`),

    getFinishedResponses: (feedbackId: number, courseId: number) =>
      req<any>(`/feedback/${feedbackId}/finished-responses?courseId=${courseId}`),

    getItems: (feedbackId: number, courseId: number) =>
      req<{ items: MoodleFeedbackItem[]; warnings?: any[] }>(
        `/feedback/${feedbackId}/items?courseId=${courseId}`
      ),

    submitPage: (
      feedbackId: number,
      responses: { name: string; value: string }[],
      courseId: number,
      page: number = 0
    ) =>
      req<unknown>(`/feedback/${feedbackId}/submit?courseId=${courseId}`, {
        method: 'POST',
        body: JSON.stringify({ responses, page }),
      }),

    launch: (feedbackId: number, courseId: number) =>
      req<{ gopage: number }>(`/feedback/${feedbackId}/launch?courseId=${courseId}`, {
        method: 'POST',
      }),
  },

  // ── Tool Responses ───────────────────────────────────────────────────────────
  tools: {
    saveResponse: (payload: {
      courseId: number;
      activityId: number;
      toolType: string;
      status: 'in_progress' | 'completed';
      screenIndex?: number;
      responses?: Record<string, unknown>;
      data?: Record<string, unknown> | null;
      // Completion-only fields — extracted from schema so the full object isn't sent
      modname?: string;
      feedbackId?: number | null;
      feedbackMapping?: Record<string, string>;
    }) =>
      req<{ success: boolean; id: string }>('/tool-response', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),

    getResponse: (courseId: number, activityId: number) =>
      req<{
        status: 'in_progress' | 'completed' | null;
        screenIndex: number;
        responses: Record<string, unknown>;
        data: Record<string, unknown> | null;
        completedAt: string | null;
      }>(`/tool-response?courseId=${courseId}&activityId=${activityId}`),

    getCrossToolResponse: (courseId: number, toolType: string) =>
      req<{
        status: 'in_progress' | 'completed' | null;
        data: Record<string, unknown> | null;
        completedAt: string | null;
      }>(`/tool-response?courseId=${courseId}&toolType=${encodeURIComponent(toolType)}`),
  },

  // ── H5P ─────────────────────────────────────────────────────────────────────
  h5p: {
    getLaunchUrl: (cmId: number, courseId: number) => {
      const params = new URLSearchParams({
        cmId: cmId.toString(),
        courseId: courseId.toString(),
      });

      return req<{ launchUrl: string; expiresAt: number; cmId: number; courseId: number }>(
        `/h5p/launch?${params.toString()}`
      );
    },
  },
};
